/**
 * ATMIVA Find Your Atmosphere
 *
 * A custom element would only upgrade once per page load, which breaks when
 * Shopify's theme editor re-renders a section. This binds explicitly instead:
 * once on DOMContentLoaded and again on every shopify:section:load, with a
 * per-element flag so listeners are never attached twice.
 *
 * Markup contract:
 *   [data-atmiva-mood-finder]          wrapper
 *   button[data-atmiva-mood="calm"]    mood control (type="button")
 *   [data-atmiva-mood-panel="calm"]    matching recommendation panel
 *   [data-atmiva-add-bundle]           add-both-to-cart button
 *   [data-atmiva-mood-target="calm"]   any link elsewhere that selects a mood
 */
(function () {
  'use strict';

  const BOUND = 'atmivaMoodBound';

  function panelsOf(root) {
    return Array.from(root.querySelectorAll('[data-atmiva-mood-panel]'));
  }

  function buttonsOf(root) {
    return Array.from(root.querySelectorAll('[data-atmiva-mood]'));
  }

  function select(root, mood, { focus = false } = {}) {
    const buttons = buttonsOf(root);
    const panels = panelsOf(root);
    let matched = false;

    buttons.forEach((button) => {
      const isMatch = button.dataset.atmivaMood === mood;
      if (isMatch) matched = true;
      button.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      button.setAttribute('tabindex', isMatch ? '0' : '-1');
      button.classList.toggle('is-selected', isMatch);
      if (isMatch && focus) button.focus();
    });

    panels.forEach((panel) => {
      panel.hidden = panel.dataset.atmivaMoodPanel !== mood;
    });

    return matched;
  }

  function onKeyDown(root, event, index) {
    const buttons = buttonsOf(root);
    const last = buttons.length - 1;
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    select(root, buttons[next].dataset.atmivaMood, { focus: true });
  }

  function cartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function addBundle(event, button) {
    event.preventDefault();
    if (button.getAttribute('aria-disabled') === 'true') return;

    let items;
    try {
      items = JSON.parse(button.dataset.items || '[]');
    } catch (error) {
      console.error('ATMIVA: could not parse bundle items', error);
      return;
    }
    if (!items.length) return;

    const panel = button.closest('[data-atmiva-mood-panel]') || document;
    const status = panel.querySelector('[data-atmiva-mood-status]');
    const spinner = button.querySelector('.loading__spinner');

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    if (spinner) spinner.classList.remove('hidden');
    if (status) status.textContent = '';

    const cart = cartElement();
    const body = { items: items, sections_url: window.location.pathname };

    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
    }

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          if (status) status.textContent = response.description || response.message;
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'atmiva-mood-finder', cartData: response });
        }

        if (cart && typeof cart.renderContents === 'function') {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(response);
        } else {
          window.location = window.routes.cart_url;
        }
      })
      .catch((error) => {
        console.error(error);
        if (status) status.textContent = 'Something went wrong. Please try again.';
      })
      .finally(() => {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
      });
  }

  function init(scope) {
    const roots = (scope || document).querySelectorAll('[data-atmiva-mood-finder]');

    roots.forEach((root) => {
      if (root.dataset[BOUND] === 'true') return;
      root.dataset[BOUND] = 'true';

      const buttons = buttonsOf(root);

      buttons.forEach((button, index) => {
        // Guarantees the control can never submit a surrounding form.
        button.setAttribute('type', 'button');
        button.addEventListener('click', (event) => {
          event.preventDefault();
          select(root, button.dataset.atmivaMood);
        });
        button.addEventListener('keydown', (event) => onKeyDown(root, event, index));
      });

      root.querySelectorAll('[data-atmiva-add-bundle]').forEach((button) => {
        button.addEventListener('click', (event) => addBundle(event, button));
      });

      // Open on whichever mood is already marked selected, else the first.
      const preselected = buttons.find((b) => b.getAttribute('aria-selected') === 'true');
      if (buttons.length) select(root, (preselected || buttons[0]).dataset.atmivaMood);
    });
  }

  /** Scroll to the mood finder and select a mood. Used by links and cards. */
  function goToMood(mood) {
    const root = document.querySelector('[data-atmiva-mood-finder]');
    const anchor = document.getElementById('atmiva-mood-finder') || root;
    if (!anchor) return false;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    anchor.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    if (mood && root) select(root, mood);
    return true;
  }

  document.addEventListener('click', (event) => {
    // Cards and buttons that name a mood explicitly.
    const moodLink = event.target.closest('[data-atmiva-mood-target]');
    if (moodLink) {
      if (goToMood(moodLink.dataset.atmivaMoodTarget)) event.preventDefault();
      return;
    }

    // Any link pointing at the mood finder anchor, including header navigation.
    const anchorLink = event.target.closest('a[href*="#atmiva-mood-finder"]');
    if (anchorLink) {
      const url = new URL(anchorLink.href, window.location.origin);
      // Only intercept when the target is this page; otherwise let it navigate.
      if (url.pathname === window.location.pathname && document.getElementById('atmiva-mood-finder')) {
        if (goToMood(null)) {
          event.preventDefault();
          history.replaceState(null, '', '#atmiva-mood-finder');
        }
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Shopify theme editor re-renders sections; rebind the fresh markup.
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window.AtmivaMoodFinder = { init: init, goToMood: goToMood };
})();
